// Job detail — the full definition of one deployable unit, with a link back
// to its live service page when deployment records exist.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Construction as JobsIcon,
  ContentCopy as CopyIcon,
  GitHub as GitHubIcon,
  GridView as ServicesIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { distinctVersions, sortEnvNames } from '../lib/grouping';
import { historicalKey, serviceHref } from '../lib/records';
import { getColors, rounded } from '../theme/tokens';
import { JobDefinition } from '../../types/DeploymentRecord';
import PageHeader from '../components/PageHeader';
import TypeBadge from '../components/TypeBadge';
import EmptyState from '../components/EmptyState';
import RollbackBreachesTab from '../components/RollbackBreachesTab';
import RollbackProfilesSection from '../components/RollbackProfilesSection';
import RollbackWatchesTab from '../components/RollbackWatchesTab';
import StackModulesSection from '../components/StackModulesSection';

const FIELD_LABELS: Array<{ key: keyof JobDefinition; label: string }> = [
  { key: 'release_package', label: 'Release package' },
  { key: 'job_folder', label: 'Job folder' },
  { key: 'git_main_branch', label: 'Main branch' },
  { key: 'repo_type', label: 'Repo type' },
  // Lambda
  { key: 'lambda_name', label: 'Lambda name' },
  { key: 'arch', label: 'Architecture' },
  { key: 'artifact', label: 'Artifact' },
  { key: 'package_name', label: 'Package' },
  // ECS
  { key: 'ecs_service_name', label: 'ECS service' },
  { key: 'ecs_cluster_name', label: 'ECS cluster' },
  { key: 'ecs_taskdef_name', label: 'Task definition' },
  { key: 'image_name', label: 'Image' },
  { key: 'service_name', label: 'Service name' },
  // Terraform
  { key: 'terraform_path', label: 'Terraform path' },
  { key: 'stack_name', label: 'Stack' },
  { key: 'task_name', label: 'Task' },
  // Static
  { key: 'asset_name', label: 'Asset' },
  { key: 'asset_type', label: 'Asset type' },
  // Adhoc
  { key: 'ssm_parameter', label: 'SSM parameter' },
];

// Mirrors the classic Jobs view (frontend/src/components/Jobs.tsx). Selected
// version/env are substituted in; until chosen, literal `{version}`/`{env}`
// placeholders remain so the command is still copy-and-edit usable.
const getDeployCommand = (job: JobDefinition, version?: string, env?: string): string => {
  const base = `jnlocal deploy ${job.type} --app ${job.app_name} --job-id ${job.job_id} --version ${version || '{version}'} --env ${env || '{env}'}`;
  return job.type === 'adhoc' ? `${base} --async --wait` : base;
};

const ROLLBACK_TABS = [
  { value: 'profile', label: 'Evaluation profile' },
  { value: 'summaries', label: 'Results summaries' },
  { value: 'breaches', label: 'Breaches' },
] as const;
type RollbackTab = (typeof ROLLBACK_TABS)[number]['value'];

// The tab lives in ?tab= beside ?type=. Only the open panel is mounted, so each
// tab asks the backend for its data the first time it is opened.
const RollbackSection: React.FC<{ app: string; jobId: string }> = ({ app, jobId }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get('tab');
  const tab: RollbackTab = ROLLBACK_TABS.find(t => t.value === asked)?.value || 'profile';
  const select = (value: RollbackTab) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    });

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mt: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
        maxWidth: 960,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: c.ink }}>Auto-rollback</Typography>
      <Typography variant="body2" sx={{ color: c.inkSecondary, mt: 0.5 }}>
        After each deploy, the auto-rollback watch checks this job&apos;s health signals for a while. The{' '}
        <strong>evaluation profile</strong> is what it checks against, <strong>results summaries</strong> are what
        each watch measured, and <strong>breaches</strong> are the watches where a signal crossed a rule, with the
        decision that followed.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v: RollbackTab) => select(v)}
        sx={{ mb: 2, borderBottom: `1px solid ${c.hairline}` }}
      >
        {ROLLBACK_TABS.map(t => (
          <Tab key={t.value} value={t.value} label={t.label} id={`rollback-tab-${t.value}`} />
        ))}
      </Tabs>
      <Box role="tabpanel" aria-labelledby={`rollback-tab-${tab}`}>
        {tab === 'profile' && <RollbackProfilesSection app={app} jobId={jobId} />}
        {tab === 'summaries' && (
          <RollbackWatchesTab app={app} jobId={jobId} onShowBreaches={() => select('breaches')} />
        )}
        {tab === 'breaches' && <RollbackBreachesTab app={app} jobId={jobId} />}
      </Box>
    </Paper>
  );
};

const JobPage: React.FC = () => {
  const { app = '', jobId = '' } = useParams<{ app: string; jobId: string }>();
  // job_id arrives without its type prefix, so lambda:x and terraform:x share
  // a URL; ?type= picks one, and without it the first match wins.
  const [searchParams] = useSearchParams();
  const jobType = searchParams.get('type') || undefined;
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { jobs, jobsLoading, jobsLoaded, fetchJobs, wsStatus, records, fetchHistorical, historical } =
    useDeploymentData();

  useEffect(() => {
    if (wsStatus === 'connected' && !jobsLoaded && !jobsLoading) fetchJobs();
  }, [wsStatus, jobsLoaded, jobsLoading, fetchJobs]);

  const job = useMemo(
    () => jobs.find(j => j.app_name === app && j.job_id === jobId && (!jobType || j.type === jobType)),
    [jobs, app, jobId, jobType]
  );

  // Records for this job (current snapshot). History is fetched separately for
  // the version/env pickers in the Deployment section.
  const jobRecords = useMemo(
    () => records.filter(r => r.app === app && (r.serviceName === jobId || r.job_id === jobId)),
    [records, app, jobId]
  );
  const hasDeployments = jobRecords.length > 0;

  // Full service string the backend keys history by (type:app/service).
  const fullService = job ? `${job.type}:${app}/${jobId}` : '';
  const historySlice = historical.get(historicalKey(app, fullService));

  // Fetch deployment history once connected, to populate the version picker.
  useEffect(() => {
    if (wsStatus === 'connected' && fullService) {
      fetchHistorical(app, fullService, 25);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsStatus, app, fullService]);

  // Version options: distinct versions across history + current records.
  const versionOptions = useMemo(
    () => distinctVersions([...(historySlice?.records || []), ...jobRecords], 25),
    [historySlice, jobRecords]
  );

  // Env options: distinct envs this job has actually deployed to.
  const envOptions = useMemo(() => {
    const set = new Set<string>();
    [...(historySlice?.records || []), ...jobRecords].forEach(r => {
      const env = r.envName || r.env;
      if (env) set.add(env);
    });
    return sortEnvNames(Array.from(set));
  }, [historySlice, jobRecords]);

  const [version, setVersion] = useState('');
  const [env, setEnv] = useState('');
  const [copied, setCopied] = useState(false);

  const crumbs = [
    { label: 'Jobs', to: '/jobs' },
    { label: app, to: `/jobs?apps=${encodeURIComponent(app)}` },
    { label: jobId },
  ];

  if (!job) {
    return (
      <Box>
        <PageHeader crumbs={crumbs} title={jobId} />
        <EmptyState
          icon={<JobsIcon />}
          title={jobsLoading || !jobsLoaded ? 'Loading job catalog…' : 'Job not found'}
          body={
            jobsLoading || !jobsLoaded
              ? undefined
              : 'No catalog entry matches this app and job ID.'
          }
          action={
            <Button component={RouterLink} to="/jobs" variant="outlined" size="small">
              Back to jobs
            </Button>
          }
        />
      </Box>
    );
  }

  const fields = FIELD_LABELS.filter(f => job[f.key] !== undefined && job[f.key] !== '');

  const deployCommand = getDeployCommand(job, version, env);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deployCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Box>
      <PageHeader
        crumbs={crumbs}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {job.job_id}
            <TypeBadge type={job.type} />
            {job.is_prod && (
              <Typography variant="caption" sx={{ color: '#d93a3a', fontWeight: 700 }}>
                PROD
              </Typography>
            )}
          </Box>
        }
        subtitle={job.app_name}
        actions={
          hasDeployments ? (
            <Button
              component={RouterLink}
              to={serviceHref(app, jobId, job.type)}
              size="small"
              variant="outlined"
              startIcon={<ServicesIcon sx={{ fontSize: 15 }} />}
            >
              View deployments
            </Button>
          ) : undefined
        }
      />

      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: `${rounded.lg}px`, borderColor: c.hairline, maxWidth: 720 }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {job.git_url && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block' }}>
                Repository
              </Typography>
              <MuiLink
                href={job.git_url}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem' }}
              >
                <GitHubIcon sx={{ fontSize: 15 }} />
                {job.git_url}
              </MuiLink>
            </Box>
          )}
          {fields.map(f => (
            <Box key={String(f.key)}>
              <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block' }}>
                {f.label}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.82rem' }}>
                {String(job[f.key])}
              </Typography>
            </Box>
          ))}
          {job.deployable_accounts && job.deployable_accounts.length > 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block' }}>
                Deployable accounts
              </Typography>
              <Typography variant="body2">{job.deployable_accounts.join(', ')}</Typography>
            </Box>
          )}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block', mb: 0.75 }}>
              Deployment
            </Typography>

            {/* Pick a version and env to build the jnlocal deploy command. */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
              <Select
                size="small"
                displayEmpty
                value={version}
                onChange={e => setVersion(e.target.value)}
                disabled={versionOptions.length === 0}
                sx={{ fontSize: '0.8rem', minWidth: 200 }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                  {historySlice?.loading && versionOptions.length === 0
                    ? 'Loading versions…'
                    : versionOptions.length === 0
                    ? 'No version history'
                    : 'Select version'}
                </MenuItem>
                {versionOptions.map(v => (
                  <MenuItem
                    key={v}
                    value={v}
                    sx={{ fontSize: '0.8rem', fontFamily: 'ui-monospace, Menlo, monospace' }}
                  >
                    {v}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                displayEmpty
                value={env}
                onChange={e => setEnv(e.target.value)}
                disabled={envOptions.length === 0}
                sx={{ fontSize: '0.8rem', minWidth: 140 }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                  {envOptions.length === 0 ? 'No envs' : 'Select env'}
                </MenuItem>
                {envOptions.map(e => (
                  <MenuItem key={e} value={e} sx={{ fontSize: '0.8rem' }}>
                    {e}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                p: 1,
                borderRadius: `${rounded.md}px`,
                backgroundColor: c.canvasSoft,
                border: `1px solid ${c.hairline}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flexGrow: 1,
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: '0.8rem',
                  overflowWrap: 'anywhere',
                }}
              >
                {deployCommand}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy deploy command'} arrow>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{ p: 0.25, color: copied ? '#10b981' : 'inherit' }}
                >
                  <CopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {(!version || !env) && (
              <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mt: 0.5 }}>
                Pick a version and env to complete the command.
              </Typography>
            )}
          </Box>
          {job.parameters && job.parameters.length > 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block', mb: 0.5 }}>
                Parameters
              </Typography>
              {job.parameters.map(p => (
                <Typography key={p.name} variant="body2" sx={{ mb: 0.25 }}>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {p.name}
                  </Box>
                  {p.default !== undefined ? ` (default: ${p.default})` : ''} — {p.description}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {job.type === 'terraform' && <StackModulesSection app={app} jobId={jobId} />}

      {/* Profiles are keyed by app/job_id without type and exist only for these
          (deploy.yaml's profile-update gate), so other types would show a
          same-named lambda's profile. */}
      {(job.type === 'lambda' || job.type === 'ecs') && <RollbackSection app={app} jobId={jobId} />}
    </Box>
  );
};

export default JobPage;
